import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, TrendingDown } from "lucide-react";
import { api, gbp, formatApiErrorDetail } from "../lib/api";
import { auctionPrice, nextDropIn, auctionStartsIn, fmtSecs } from "../lib/auction";
import { useAuth } from "../context/AuthContext";

export default function AuctionDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isMember } = useAuth();
  const [a, setA] = useState(null);
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [, force] = useState(0);

  const load = useCallback(() => {
    api.get(`/auctions/${slug}`).then(({ data }) => setA(data)).catch(() => navigate("/reverse-auction"));
  }, [slug, navigate]);

  useEffect(() => { load(); const p = setInterval(load, 8000); return () => clearInterval(p); }, [load]);
  useEffect(() => { const t = setInterval(() => force((n) => n + 1), 1000); return () => clearInterval(t); }, []);

  if (!a) return <div className="max-w-7xl mx-auto px-8 py-32 font-display text-3xl text-white/30">LOADING AUCTION…</div>;

  const live = a.state === "live" && !order;
  const scheduled = a.state === "scheduled" && !order;
  const price = live ? auctionPrice(a) : scheduled ? a.start_price : (order ? order.total : a.claimed_price);
  const pct = ((a.start_price - price) / (a.start_price - a.floor_price)) * 100;

  const claim = async () => {
    if (!isMember) { navigate("/join"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/auctions/${a.id}/claim`);
      setOrder(data);
      toast.success(`Claimed at ${gbp(data.total)}. The lot is yours.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <div className="grid lg:grid-cols-2 gap-12">
        <div className="border border-[#27272A] overflow-hidden h-fit">
          <img src={a.images[0]} alt={a.title} className="w-full aspect-[4/3] object-cover" data-testid="auction-detail-image" />
        </div>
        <div>
          <div className="flex items-center gap-2 text-[#FF3B30] text-xs uppercase tracking-[0.3em] mb-3">
            <TrendingDown size={12} /> Reverse auction · {a.sku}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl leading-[0.95]" data-testid="auction-detail-title">{a.title}</h1>
          <div className="text-white/40 text-xs uppercase tracking-widest mt-3">{a.condition} · {a.units} units</div>
          <p className="text-white/50 text-sm leading-relaxed mt-6">{a.description}</p>

          <div className={`border-2 p-8 mt-8 ${live ? "border-[#FF3B30]" : "border-[#27272A]"}`}>
            <div className="text-[10px] uppercase tracking-widest text-white/40">{scheduled ? "Starting price" : "Current price"}</div>
            <div data-testid="auction-current-price" className={`font-display text-6xl sm:text-7xl ${live || scheduled ? "text-[#FFCC00]" : "text-white/40"}`}>{price != null ? gbp(price) : "—"}</div>
            {scheduled && (
              <div className="mt-4 font-display text-2xl text-[#FF3B30]" data-testid="auction-starts-in">DROPS BEGIN IN {fmtSecs(auctionStartsIn(a))}</div>
            )}
            {live && (
              <div className="flex justify-between items-center mt-4 text-xs">
                <span className="text-white/40">Drops {gbp(a.drop_amount)} every {a.interval_seconds}s</span>
                <span className="font-display text-2xl text-[#FF3B30]" data-testid="auction-next-drop">NEXT DROP {nextDropIn(a)}S</span>
              </div>
            )}
            <div className="mt-4 h-2 bg-[#27272A]">
              <div className="h-full bg-[#FF3B30] transition-[width] duration-1000" style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
            </div>
            <div className="flex justify-between text-[10px] uppercase tracking-widest text-white/30 mt-2">
              <span>Started {gbp(a.start_price)}</span><span>Floor {gbp(a.floor_price)}</span>
            </div>
          </div>

          {order ? (
            <div className="border-2 border-[#FFCC00] p-8 mt-6" data-testid="auction-claim-success">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle2 size={28} className="text-[#FFCC00]" />
                <h2 className="font-display text-3xl">CLAIMED. IT'S YOURS.</h2>
              </div>
              <div className="text-xs uppercase tracking-widest text-white/40 mb-1">Payment reference</div>
              <div className="font-display text-3xl text-[#FFCC00]" data-testid="auction-order-reference">{order.reference}</div>
              <p className="text-white/50 text-xs mt-4">
                Pay {gbp(order.total)} by bank transfer to {order.bank_details.account_name} — sort code {order.bank_details.sort_code}, account {order.bank_details.account_number}. Full details emailed to you and in <Link to="/account" className="text-[#FFCC00] underline">your account</Link>.
              </p>
            </div>
          ) : live ? (
            <button data-testid="auction-claim-button" onClick={claim} disabled={busy} className="btn-industrial w-full py-5 text-2xl mt-6">
              {busy ? "CLAIMING…" : isMember ? `CLAIM NOW AT ${gbp(price)}` : "JOIN FREE TO CLAIM"}
            </button>
          ) : scheduled ? (
            <div className="mt-6 border border-[#FFCC00] p-6 text-center font-display text-2xl text-[#FFCC00]" data-testid="auction-scheduled-banner">
              NOT LIVE YET — COME BACK WHEN THE PRICE STARTS FALLING.
            </div>
          ) : a.state === "ended" ? (
            <div className="mt-6 border border-[#27272A] p-6 text-center font-display text-3xl text-white/40" data-testid="auction-ended-banner">
              AUCTION ENDED — UNCLAIMED.
            </div>
          ) : (
            <div className="mt-6 border border-[#FF3B30] p-6 text-center font-display text-3xl text-[#FF3B30]" data-testid="auction-claimed-banner">
              CLAIMED BY {(a.claimed_by_name || "A MEMBER").split(" ")[0].toUpperCase()} AT {gbp(a.claimed_price)}.
            </div>
          )}
          <p className="text-white/30 text-[11px] mt-4">First claim wins the entire lot. Server sets the price — no take-backs, no re-runs. See <Link to="/legal/terms" className="underline">auction rules</Link>.</p>
        </div>
      </div>
    </div>
  );
}
